import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { format } from 'date-fns'
import { makeStyles, Typography, Paper, CardMedia, Grid, Collapse } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import useGetLocalStorage from '../../../hooks/useGetLocalStorage'
import { useHistory, useLocation } from 'react-router-dom'
import localStorageIds from '../../../utils/constants/localStorageIds'
import getStudioGames from '../../../Apis/studio/getStudioGames'
import ContainerPadding from '../../../components/containerPadding/containerPadding'
import LoaderDots from '../../../components/loaders/LoaderDots/LoaderDots'
import DefaultOrgIcon from '../../../assets/icons/DefaultOrgIcon'
import COLORS from '../../../utils/constants/colors'

const studioPageStyles = {
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontWeight: 600,
  },
  game: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    marginBottom: '8px',
  },
  gameClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: COLORS.hoverGrey,
    },
  },
  gameDate: {
    width: '180px',
    flexShrink: 0,
  },
  teams: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: '8px',
  },
  teamLogo: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundSize: 'contain',
  },
  versus: {
    margin: '0 8px',
    color: COLORS.grey,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0,
    marginBottom: '12px',
    '& svg': { color: COLORS.grey },
  },
  place: {
    width: '180px',
    flexShrink: 0,
    textAlign: 'right',
    color: COLORS.grey,
  },
  liveBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
    marginLeft: '14px',
    padding: '3px 9px',
    borderRadius: '4px',
    background: COLORS.red,
    color: COLORS.white,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.6px',
  },
  liveDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    background: COLORS.white,
    animation: '$liveBlink 1.4s infinite',
  },
  '@keyframes liveBlink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.3 },
  },
}

const useStyles = makeStyles(studioPageStyles)

const TeamLogo = ({ logoUrl, className }) =>
  logoUrl ? (
    <CardMedia className={className} image={logoUrl} />
  ) : (
    <DefaultOrgIcon className={className} />
  )

const GameRow = ({ game, classes, onSelect, live, clickable = true }) => {
  // Prefer eventTeams — `teams` drops ad-hoc teams not linked to a Team entity; home first.
  const gameTeams = game.eventTeams?.length ? game.eventTeams : game.teams || []
  const [firstTeam, secondTeam] = [...gameTeams].sort(
    (a, b) => (b?.isHomeTeam ? 1 : 0) - (a?.isHomeTeam ? 1 : 0),
  )
  const formattedDate = game.startDateTime
    ? format(new Date(game.startDateTime), 'dd MMM yyyy, HH:mm')
    : ''

  return (
    <Paper
      className={`${classes.game} ${clickable ? classes.gameClickable : ''}`}
      onClick={clickable ? onSelect : undefined}
    >
      <Typography className={classes.gameDate} variant='body2'>
        {formattedDate}
      </Typography>
      <div className={classes.teams}>
        <TeamLogo logoUrl={firstTeam?.logoUrl} className={classes.teamLogo} />
        <Typography noWrap variant='subtitle2'>
          {firstTeam?.name}
        </Typography>
        <span className={classes.versus}>vs</span>
        <TeamLogo logoUrl={secondTeam?.logoUrl} className={classes.teamLogo} />
        <Typography noWrap variant='subtitle2'>
          {secondTeam?.name}
        </Typography>
      </div>
      <Typography className={classes.place} variant='body2'>
        {game.place}
      </Typography>
      {live && (
        <span className={classes.liveBadge}>
          <span className={classes.liveDot} />
          LIVE
        </span>
      )}
    </Paper>
  )
}

// Collapsible, counted section used for all three groups. Always renders its header — "<title>
// (n)" with a chevron — even at 0 games; rows live in a Collapse. defaultOpen controls the
// initial state (Live/Upcoming open, Past collapsed); clickable/live are forwarded to the rows.
const GameSection = ({
  title,
  games,
  classes,
  onSelectGame,
  live,
  clickable = true,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen)
  const list = games || []
  return (
    <div className={classes.section}>
      <button
        type='button'
        className={classes.sectionHeader}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        <Typography variant='h6' className={classes.sectionTitle}>
          {title} ({list.length})
        </Typography>
      </button>
      <Collapse in={open}>
        {list.map((game) => (
          <GameRow
            key={game.id}
            game={game}
            classes={classes}
            live={live}
            clickable={clickable}
            onSelect={() => onSelectGame(game)}
          />
        ))}
      </Collapse>
    </div>
  )
}

const StudioPage = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const localStorageValue = useGetLocalStorage()
  const { [localStorageIds.SELECTED_ORG]: selectedOrg } = localStorageValue || {}
  const orgId = selectedOrg?.id

  // Pass the full game in navigation state so the game page can show its title
  // immediately (no second fetch needed on the click-through path). Carry the search
  // string through too: org/team live in the URL, and every later navigation copies
  // them forward from wherever it currently is, so dropping them here leaves the
  // calendar with no org and silently no events until a full page reload restores them.
  const onSelectGame = (game) =>
    history.push({
      pathname: `/studio/games/${game.id}`,
      search: location.search,
      state: { studioGame: game },
    })

  const { data, isFetching } = useQuery(['getStudioGames', orgId], () => getStudioGames(orgId), {
    enabled: Boolean(orgId),
  })

  if (isFetching) {
    return (
      <Grid container justifyContent='center' style={{ marginTop: '100px' }}>
        <LoaderDots style={{ fill: COLORS.orange, width: 70, height: 70 }} />
      </Grid>
    )
  }

  // Each game carries its stream state in broadcast.state (running | paused | finished | …).
  // Live (running/paused) is surfaced at the top in its own group. Upcoming is future games
  // (minus any that are live). Past holds ONLY finished games — successfully-streamed history —
  // so errored/deleting/cancelled/never-streamed games don't clutter it; it's collapsed behind a
  // toggle whose count tells the operator how many games they've completed.
  const streamState = (g) => g?.broadcast?.state
  const isLiveGame = (g) => streamState(g) === 'running' || streamState(g) === 'paused'
  const liveGames = [...(data?.upcoming || []), ...(data?.past || [])]
    .filter(isLiveGame)
    .sort((a, b) => new Date(b.startDateTime) - new Date(a.startDateTime))
  const upcomingGames = (data?.upcoming || []).filter((g) => !isLiveGame(g))
  const pastGames = (data?.past || []).filter((g) => streamState(g) === 'finished')

  return (
    <ContainerPadding paddingValue='1.5%' vertical>
      <GameSection
        title='Live'
        games={liveGames}
        classes={classes}
        onSelectGame={onSelectGame}
        live
      />
      <GameSection
        title='Upcoming'
        games={upcomingGames}
        classes={classes}
        onSelectGame={onSelectGame}
      />
      <GameSection
        title='Past'
        games={pastGames}
        classes={classes}
        onSelectGame={onSelectGame}
        clickable={false}
        defaultOpen={false}
      />
    </ContainerPadding>
  )
}

export default StudioPage
